/**
 * Custom hook to preload service history for all trucks on page load
 */

import { useEffect } from 'react';
import { customerService } from '../../../../services/customer.service';
import type { Truck } from '../types';

export const usePreloadServiceHistory = (
  trucks: Truck[],
  setTrucks: React.Dispatch<React.SetStateAction<Truck[]>>
) => {
  useEffect(() => {
    const loadAllServiceHistory = async () => {
      if (trucks.length === 0) return;

      // Load service history for all trucks in parallel
      const promises = trucks.map(async (truck) => {
        // Skip if already loaded
        if (truck.serviceHistory) return null;

        try {
          const history = await customerService.getServiceHistory(truck.id);
          return { truckId: truck.id, history };
        } catch (error) {
          console.error(`Failed to load service history for truck ${truck.id}:`, error);
          return null;
        }
      });

      const results = await Promise.all(promises);

      // Update trucks state with loaded service history
      setTrucks((prevTrucks) =>
        prevTrucks.map((truck) => {
          const result = results.find((r) => r?.truckId === truck.id);
          if (result) {
            return { ...truck, serviceHistory: result.history };
          }
          return truck;
        })
      );
    };

    loadAllServiceHistory();
  }, [trucks.length]); // Only run when number of trucks changes
};
